import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/site';
import { categories, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formErrorMsg, setFormErrorMsg] = useState('');

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://xinxinmanhua.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      foundingDate: siteInfo.founded,
      contactPoint: {
        '@type': 'ContactPoint',
        email: siteInfo.contact.email,
        contactType: 'customer service',
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9on7vqqs38sj6jc7bug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (
        serverMsg.includes('spam') ||
        (parsed && parsed.code !== 'OK')
      ) {
        setFormStatus('error');
        setFormErrorMsg(serverMsg || '提交失败，请稍后重试');
        return;
      }

      setFormStatus('success');
      form.reset();
    } catch {
      setFormStatus('error');
      setFormErrorMsg('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            </div>

            {/* Hero */}
            <section className="relative rounded-2xl overflow-hidden mb-16 h-[300px] md:h-[400px] flex items-center justify-center">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20golden%20orange%20abstract%20gradient%20with%20soft%20ink%20wash%20texture%2C%20artistic%20minimal%20composition%2C%20elegant%20atmosphere%2C%20Japanese%20sumi-e%20inspired%20subtle%20brush%20strokes%2C%20warm%20ambient%20light%2C%20professional%20website%20header%20background&width=1400&height=600&seq=about-hero-2026&orientation=landscape"
                alt="关于新新漫画"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
              <div className="relative z-10 text-center px-4">
                <h1 className="text-3xl md:text-5xl font-bold text-white font-heading mb-4">
                  关于新新漫画
                </h1>
                <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto">
                  {siteInfo.mission}
                </p>
              </div>
            </section>

            {/* Brand Intro */}
            <section className="mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    新新漫画成立于{siteInfo.founded}，是一个专注于漫画分类与推荐的导航平台。我们致力于为漫画爱好者提供全面、精准、便捷的漫画发现体验。
                  </p>
                  <p className="text-foreground-600 leading-relaxed mb-4">
                    在漫画市场蓬勃发展的今天，韩漫以其精美的全彩条漫格式和紧凑的剧情吸引了大量读者；日漫凭借深厚的分镜功力和丰富的题材始终保持经典地位；国漫则异军突起，以独特的中国风格和创意内容赢得了广泛认可。
                  </p>
                  <p className="text-foreground-600 leading-relaxed">
                    新新漫画汇集三大漫画流派的精华，通过科学的分类体系和智能推荐，让每一位漫画爱好者都能快速找到自己喜爱的作品。
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {siteInfo.values.map((val, i) => (
                    <div key={i} className="bg-white border border-background-200 rounded-xl p-5">
                      <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center mb-3">
                        <i className="ri-check-double-line text-primary-500 text-lg" />
                      </div>
                      <h3 className="font-semibold text-foreground-900 text-sm mb-1">{val.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{val.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="mb-16">
              <div className="bg-white border border-background-200 rounded-2xl p-8 md:p-12">
                <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-10">
                  新新漫画的口碑
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {siteInfo.stats.map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className="text-3xl md:text-4xl font-bold text-primary-500 font-heading mb-2">
                        {stat.value}
                      </div>
                      <div className="text-sm text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Categories Quick Links */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-8">
                探索漫画分类
              </h2>
              <div className="flex flex-wrap justify-center gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-5 py-2.5 bg-white border border-background-200 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-500 hover:bg-primary-50 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} mr-1.5`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-16">
              <div className="bg-white border border-background-200 rounded-2xl p-8 md:p-12 max-w-2xl mx-auto">
                <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-2">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm text-center mb-8">
                  有任何问题或建议，欢迎随时与我们联系
                </p>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="website-alt-wrapper">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    提交留言
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-center text-sm text-secondary-600 bg-secondary-50 rounded-lg py-3 px-4">
                      <i className="ri-checkbox-circle-fill mr-1.5" />
                      留言已成功提交，我们会尽快回复！
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="text-center text-sm text-accent-600 bg-accent-50 rounded-lg py-3 px-4">
                      <i className="ri-error-warning-fill mr-1.5" />
                      {formErrorMsg || '提交失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-16">
              <h2 className="text-2xl font-bold text-foreground-900 font-heading text-center mb-8">
                常见问题
              </h2>
              <div className="max-w-2xl mx-auto space-y-3">
                {faqs.slice(0, 4).map((faq, index) => (
                  <div key={index} className="border border-background-200 rounded-lg overflow-hidden bg-white">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <span className="font-medium text-foreground-800 text-sm pr-4">{faq.question}</span>
                      <i className={`text-foreground-400 transition-transform duration-200 flex-shrink-0 ${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'}`} />
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}